import { useState } from 'react'
import { Alert, Input, Modal, Space, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useSubscriberCountUnder } from '@/shared/api/reference'
import { closeAgent, fetchAgentSubtree, type AgentNode } from './agentApi'

interface AgentCloseModalProps {
  open: boolean
  agent: AgentNode
  onClose: () => void
}

/**
 * Agentni YOPISH — `DELETE /api/agents/{id}` (`closeAgent`).
 *
 * <h2>⚠️ NEGA BU OYNA KERAK BO'LDI</h2>
 * Backend uch holatni biladi, panelda esa faqat ikkitasi bor edi:
 * `AgentGovernance` `ACTIVE ⇄ SUSPENDED` ni almashtirardi, `closeAgent`
 * esa YOZILGAN, LEKIN HECH QAYERDAN CHAQIRILMAGAN funksiya bo'lib
 * turardi. Ya'ni «bu sherik bilan ishlashni tugatdik» degan holat
 * panelda umuman ifodalanmasdi va operator uni to'xtatib qo'yib
 * ("vaqtincha") shu holicha qoldirardi.
 *
 * <h2>⚠️ NEGA `Popconfirm` EMAS, OYNA</h2>
 * `AgentGovernance` da `ACTIVE ⇄ SUSPENDED` uchun `Popconfirm` bor va u
 * o'z o'rnida: o'sha amal QAYTARILADI. Yopish esa panelda qaytarilmaydi
 * (`AgentState` javadoc'i: «yopish qaytarilmaydi») — shuning uchun bu
 * yerda `NasDeleteModal` naqshi: oqibat matn bilan aytiladi va
 * tasdiqlash uchun tugun KODI qo'lda teriladi. Terish «keyingi»
 * tugmasini avtomatik bosishni to'xtatadi va operator QAYSI tugunni
 * yopayotganini o'z ko'zi bilan tekshiradi.
 *
 * <h2>⚠️ OQIBAT TAXMIN QILINMAYDI — O'LCHANADI</h2>
 * Ikkala son ham SERVERDAN so'raladi, mahalliy daraxtdan sanalmaydi:
 * ko'rinish doirasi butun shoxni bilmaydi va yarim javob ishonchli
 * ko'rinishda YOLG'ON bo'lardi (`AgentMoveModal` dagi bilan bir xil
 * mulohaza).
 *
 * O'lchangan xulq (`AgentServiceImpl#changeState`): so'rov FAQAT shu
 * tugunning `state` ustunini yozadi, kaskad YO'Q. Ya'ni:
 *   · ostidagi tugunlar YOPILMAYDI va sotuvni davom ettiradi;
 *   · abonentlar YOPILMAYDI, `reseller_path` ham tegilmaydi;
 *   · daraxt, ledger yozuvlari va komissiya qoidalari joyida qoladi;
 *   · shu tugunning O'ZIDA pul amali rad etiladi
 *     (`DepositServiceImpl#requireOpen` va `AgentState#canTransact`).
 * Bularning hammasi oynada aytiladi — «yopdim, ostidagilar ham
 * to'xtadi» degan taxmin eng ko'p uchraydigan yanglish bo'lardi.
 */
export function AgentCloseModal({ open, agent, onClose }: AgentCloseModalProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [typed, setTyped] = useState('')

  const subtree = useQuery({
    queryKey: queryKeys.agents.subtree(agent.id),
    queryFn: () => fetchAgentSubtree(agent.id),
    enabled: open,
    staleTime: 30_000,
  })

  // ⚠️ Javobda tugunning O'ZI ham bor (`path <@`), shuning uchun `- 1`.
  const descendants = subtree.data ? Math.max(subtree.data.length - 1, 0) : null

  const mayCountSubscribers = can('subscriber.read')
  const subscribers = useSubscriberCountUnder(agent.id, open && mayCountSubscribers)

  const close = useMutation({
    mutationFn: () => closeAgent(agent.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all })
      setTyped('')
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin (`NasDeleteModal` naqshi) */
  const cancel = () => {
    close.reset()
    setTyped('')
    onClose()
  }

  /**
   * Sonni MATNGA o'girish — noma'lumni NOL deb ko'rsatmasdan.
   *
   * ⚠️ `?? 0` yozish eng zararli soddalashtirish bo'lardi: yiqilgan
   * so'rov «hech kim ta'sirlanmaydi» bo'lib o'qilardi va operator
   * aynan shunga tayanib tugmani bosardi.
   */
  const countText = (value: number | null, loading: boolean, failed: boolean) =>
    failed ? t('agents.close.countUnknown')
    : loading || value === null ? t('common.loading')
    : String(value)

  return (
    <Modal
      open={open}
      title={t('agents.close.title')}
      onCancel={cancel}
      onOk={() => close.mutate()}
      okText={t('agents.close.submit')}
      okButtonProps={{ danger: true, disabled: typed.trim() !== agent.code }}
      cancelText={t('common.cancel')}
      confirmLoading={close.isPending}
      destroyOnHidden
      width={560}
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('agents.close.irreversibleTitle')}
        description={t('agents.close.irreversibleHint')}
      />

      <Typography.Paragraph>
        {t('agents.close.subject', { code: agent.code, name: agent.displayName })}
      </Typography.Paragraph>

      {/*
        ⚠️ IKKI SON — VA ULARNING MA'NOSI «NECHTASI YOPILADI» EMAS,
        «NECHTASI YOPILMAYDI». Aynan shu farq operatorning kutganidan
        chetlashadi, shuning uchun u raqam bilan birga yoziladi.
      */}
      <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
        <Typography.Text>
          {/* ⚠️ O'rin nomi `value`, `count` EMAS: i18next `count` ni
              ko'plik qoidasi deb o'qiydi va u yerda SON kutiladi —
              «so'rov yiqildi» kabi matn qiymat bo'la olmasdi. */}
          {t('agents.close.descendants', {
            value: countText(descendants, subtree.isPending, subtree.isError),
          })}
        </Typography.Text>
        <Typography.Text>
          {mayCountSubscribers
            ? t('agents.close.subscribers', {
                value: countText(subscribers.count, subscribers.isLoading, subscribers.isError),
              })
            : t('agents.close.subscribersNoPermission')}
        </Typography.Text>
        <Typography.Text type="secondary">{t('agents.close.effect')}</Typography.Text>
      </Space>

      {/* Xato AYNAN shu yerda: oyna ochiq qoladi va operator sababni
          tugma bilan bir ekranda ko'radi (`AgentGovernance` dagi
          limitlar oynasi bilan bir xil qoida) */}
      {close.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(close.error, t)}
        />
      ) : null}

      <Typography.Text type="secondary">
        {t('actions.typeToConfirm', { value: agent.code })}
      </Typography.Text>
      <Input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={agent.code}
        disabled={close.isPending}
        style={{ marginTop: 8 }}
      />
    </Modal>
  )
}
